/**
 * Ads for the free app on phones (Premium phase 5): one small banner above
 * the tab bar, on Home and Analytics only. Non-personalized (npa): no
 * tracking prompt, nothing about the person is used to pick an ad. Never on
 * the web, never with Premium.
 *
 * The banner is a native view drawn over the web content, so it is shown
 * only where nothing else can sit under it (see AdBanner.tsx).
 */
export interface AdsBridge {
  /**
   * Google's consent flow (UMP), before any ad: where the law asks for
   * consent (the EEA, the UK, Switzerland) the certified message is shown
   * once; answers whether ads may be requested at all.
   */
  consent(): Promise<{ canRequestAds: boolean; privacyChoices: boolean }>;
  /** Where consent applies, the person can change their choice at any time (More → Ad privacy choices). */
  openPrivacyChoices(): Promise<void>;
  /** Show (or move) the banner `bottom` px above the screen's bottom edge. */
  show(bottom: number): Promise<void>;
  hide(): Promise<void>;
  /** The banner's height as it changes (0 when hidden); returns an unsubscribe. */
  onHeight(listener: (height: number) => void): () => void;
}

/** Google's own test banners: shown in development builds only. */
export const TEST_BANNER = { ios: 'ca-app-pub-3940256099942544/2934735716', android: 'ca-app-pub-3940256099942544/6300978111' } as const;

/**
 * The banner ad unit for this build: VITE_ADMOB_BANNER_IOS / _ANDROID, or
 * Google's test unit in development. A release build without one shows no
 * ads at all (never someone else's, never a test ad).
 */
export function bannerUnit(
  platform: 'ios' | 'android',
  env: { VITE_ADMOB_BANNER_IOS?: string; VITE_ADMOB_BANNER_ANDROID?: string; DEV?: boolean } = import.meta.env,
): { adId: string; testing: boolean } | null {
  const id = (platform === 'ios' ? env.VITE_ADMOB_BANNER_IOS : env.VITE_ADMOB_BANNER_ANDROID)?.trim();
  if (id) return { adId: id, testing: false };
  return env.DEV ? { adId: TEST_BANNER[platform], testing: true } : null;
}

export function admobBridge(unit: { adId: string; testing: boolean }): AdsBridge {
  type AdMobModule = typeof import('@capacitor-community/admob');
  let ready: Promise<AdMobModule> | null = null;
  let shownAt: number | null = null;
  let created = false;
  const listeners = new Set<(h: number) => void>();

  const admob = () =>
    (ready ??= (async () => {
      const mod = await import('@capacitor-community/admob');
      await mod.AdMob.initialize({ initializeForTesting: unit.testing });
      await mod.AdMob.addListener(mod.BannerAdPluginEvents.SizeChanged, (size) => listeners.forEach((l) => l(size.height)));
      return mod;
    })().catch((e: unknown) => {
      ready = null;
      throw e;
    }));

  let consented: Promise<{ canRequestAds: boolean; privacyChoices: boolean }> | null = null;
  const consent = () => {
    consented ??= (async () => {
      try {
        const { AdMob, AdmobConsentStatus } = await admob();
        let info = await AdMob.requestConsentInfo();
        if (info.status === AdmobConsentStatus.REQUIRED && info.isConsentFormAvailable) info = await AdMob.showConsentForm();
        return { canRequestAds: info.canRequestAds, privacyChoices: String(info.privacyOptionsRequirementStatus) === 'REQUIRED' };
      } catch (e) {
        console.warn('[ads] consent', e);
        consented = null;
        // Without an answer, no ads (never an ad where consent was not settled).
        return { canRequestAds: false, privacyChoices: false };
      }
    })();
    return consented;
  };

  return {
    consent,
    async openPrivacyChoices() {
      try {
        await (await admob()).AdMob.showPrivacyOptionsForm();
        // The choice may have changed: ask again before the next ad.
        consented = null;
      } catch (e) {
        console.warn('[ads] privacy options', e);
      }
    },
    async show(bottom) {
      if (!(await consent()).canRequestAds) return;
      try {
        const { AdMob, BannerAdPosition, BannerAdSize } = await admob();
        if (created && shownAt === bottom) return void (await AdMob.resumeBanner());
        if (created) await AdMob.removeBanner();
        await AdMob.showBanner({
          adId: unit.adId,
          isTesting: unit.testing,
          adSize: BannerAdSize.ADAPTIVE_BANNER,
          position: BannerAdPosition.BOTTOM_CENTER,
          margin: bottom,
          npa: true,
        });
        created = true;
        shownAt = bottom;
      } catch (e) {
        console.warn('[ads] banner', e);
      }
    },
    async hide() {
      if (!created) return;
      try {
        await (await admob()).AdMob.hideBanner();
        listeners.forEach((l) => l(0));
      } catch (e) {
        console.warn('[ads] hide', e);
      }
    },
    onHeight(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
